import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Empreendimento } from "../../domain/models/empreendimento";
import { CentroCustoDropDown } from "../../../../common/domain/models/centro_custo_dropdown";
import { processError } from "../../../../common/utils/base_error";
import { useForm } from "react-hook-form";
import { EmpreendimentoForm, EmpreendimentoSchema } from "../schemas/Empreendimento_cadastro/empreendimento";
import { zodResolver } from "@hookform/resolvers/zod";
import { EmpreendimentoCreate } from "../../domain/models/empreendimentoCreate";
import { EmpreendimentoBlocoDropDown } from "../../domain/models/empreendimento_bloco_dropdown";
import { EmpreendimentoBlocoCompletoTable, ListaEmpreendimentoBlocoCompletoTable } from "../../domain/models/empreendimentoBlocoTable";
import { useLocation, useNavigate } from "react-router";
import { blocoAlteracao, listBlocosAlteracao } from "../../domain/models/empreendimentoBlocosAlteracao";
import { useCreateDebounceHandler, useDebouncedCallback } from "../../../../common/hooks/use_debounced_callback";
import { listUnidadesAlteracao, unidadeAlteracao } from "../../domain/models/empreendimentoUnidadesAlteracao";
import { useChangeHistory } from "../../../../common/hooks/use_change_history";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { LogTipoTabela } from "../../../../common/utils/log_tabela_tipo";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";

export const useEmpreendimentoCadastroViewModel = () => {
    const {
        commons: {
            getCentroCustoEmpreendimentoDropDownUseCase,
            getAllLogAlteracaoUseCase,
            getAllLogAlteracaoByParentUseCase,
        },
        incorporacao: {
            copiaBlocosUseCase,
            createUnidadesBlocoUseCase,
            createEmpreendimentoUseCase,
            updateEmpreendimentoUseCase,
            getEmpreendimentoBlocoTableUseCase,
            getEmpreendimentoByIdUseCase,
            updateBlocosUseCase,
            updateUnidadesUseCase,
            deleteBlocoUseCase,
            deleteUnidadeUseCase,
            getEmpreendimentoBlocoDropdownUseCase,
        },
    } = useDependencyInjection();

    const startFlag = useRef(false);
    const location = useLocation();
    const navigate = useNavigate();
    const debouncedRef = useCreateDebounceHandler(750);

    const { pushErrorNotification, pushInfoNotification, setPageLoading } = usePageLoader();

    const changeHistory = useChangeHistory({
        getAllLogAlteracaoUseCase,
        getAllLogAlteracaoByParentUseCase,
        onSetError: (e) => pushErrorNotification('Erro', [e]),
    })

    const hookForm = useForm<EmpreendimentoForm>({
        mode: "onSubmit",
        reValidateMode: "onSubmit",
        resolver: zodResolver(EmpreendimentoSchema),

    });

    const [selectedEmpreendimento, setSelectedEmpreendimento] = useState<Empreendimento>();
    const [selectedIdEmpreendimento, setSelectedIdEmpreendimento] = useState<number | null>();

    const [selectedBlocoAlteracao, setSelectedBlocoAlteracao] = useState<blocoAlteracao>();
    const [selectedBlocoDelecao, setSelectedBlocoDelecao] = useState<blocoAlteracao>();
    const [selectedBlocoCopia, setSelectedBlocoCopia] = useState<blocoAlteracao>();
    const [selectedBlocoUnidades, setSelectedBlocoUnidades] = useState<EmpreendimentoBlocoCompletoTable>();

    const [selectedUnidadeAlteracao, setSelectedUnidadeAlteracao] = useState<unidadeAlteracao>();
    const [selectedUnidadeDelecao, setSelectedUnidadeDelecao] = useState<unidadeAlteracao>();

    const [listAlteracaoBloco, setListAlteracaoBloco] = useState<listBlocosAlteracao>({ list: [] });
    const [listAlteracaoUnidades, setListAlteracaoUnidades] = useState<listUnidadesAlteracao>({ list: [] });

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const [errorMessage, setErrorMessage] = useState<string>();

    const [listBlocoDropDown, setListBlocoDropDown] = useState<EmpreendimentoBlocoDropDown[]>([])
    const [listCentroCustoDropDown, setListCentroCustoDropDown] = useState<CentroCustoDropDown[]>()
    const [selectedBlocosDropDown, setSelectedBlocosDropDown] = useState<number[]>([])

    const [listBlocoCompleteTable, setListBlocoCompleteTable] = useState<ListaEmpreendimentoBlocoCompletoTable>()

    const [openDeleteModal, setOpenDeleteModal] = useState(false);
    const [openDeleteUnidadeModal, setOpenDeleteUnidadeModal] = useState(false);
    const [openCadastroModal, setOpenCadastroModal] = useState(false);
    const [openCopyModal, setOpenCopyModal] = useState(false);
    const [openUnidadesModal, setOpenUnidadesModal] = useState(false);

    const [idCentroCusto, tipo] = hookForm.watch(["idCentroCusto", "tipo"])

    //#region CARREGAMENTO
    const loadEmpreendimento = useCallback(async (idEmpreendimento?: number) => {
        try {
            const filteredIdEmpreendimento = idEmpreendimento ?? selectedIdEmpreendimento ?? null;
            if (filteredIdEmpreendimento === null) throw new Error("Falha ao encontrar Empreendimento!")

            const empreendimento = await getEmpreendimentoByIdUseCase.getEmpreendimentoById(filteredIdEmpreendimento)
            hookForm.setValue('idEmpreendimento', filteredIdEmpreendimento);
            hookForm.setValue('idCentroCusto', empreendimento.idCentroCusto ?? 0);
            hookForm.setValue('descricao', empreendimento.descricao ?? '');
            hookForm.setValue('apresentacao', empreendimento.apresentacao ?? '');
            hookForm.setValue('tipo', empreendimento.tipo ?? 0);
            hookForm.setValue('blocosQuantidade', 1);
            hookForm.setValue('andaresQuantidade', 1);
            hookForm.setValue('apartamentosQuantidade', 1);

            setSelectedEmpreendimento(empreendimento);
        } catch (e: unknown) {
            throw new Error("Falha ao buscar Empreendimento!")
        }
    }, [
        hookForm,
        selectedIdEmpreendimento,
        getEmpreendimentoByIdUseCase
    ])

    const loadBlocoDropDown = useCallback(async (idEmpreendimento: number | undefined) => {
        try {
            const idEmpreendimentoState = location.state?.idEmpreendimento as number | 0
            const blocos = await getEmpreendimentoBlocoDropdownUseCase.getEmpreendimentoBlocoDropdown(idEmpreendimento ?? idEmpreendimentoState);

            setListBlocoDropDown([...blocos]);
            setSelectedBlocosDropDown(blocos.map(x => x.id));
            setListBlocoCompleteTable(await getEmpreendimentoBlocoTableUseCase.getEmpreendimentoBlocoTable(blocos.map(x => x.id)))
        } catch (e: unknown) {
            throw new Error("Falha ao buscar centro de custo!");
        }
    }, [
        location.state?.idEmpreendimento,
        getEmpreendimentoBlocoDropdownUseCase,
        getEmpreendimentoBlocoTableUseCase,
    ]);

    // Recarrega o dropdown de blocos mantendo a seleção atual: blocos novos entram selecionados
    // e blocos que não existem mais saem da seleção e da tabela.
    const reloadBlocoDropDown = useCallback(async () => {
        if (!selectedIdEmpreendimento) return;

        const blocos = await getEmpreendimentoBlocoDropdownUseCase.getEmpreendimentoBlocoDropdown(selectedIdEmpreendimento);
        const idsAnteriores = listBlocoDropDown.map(x => x.id);
        const idsSelecionados = blocos
            .map(x => x.id)
            .filter(id => selectedBlocosDropDown.includes(id) || !idsAnteriores.includes(id));

        setListBlocoDropDown([...blocos]);
        setSelectedBlocosDropDown(idsSelecionados);
        setListBlocoCompleteTable(await getEmpreendimentoBlocoTableUseCase.getEmpreendimentoBlocoTable(idsSelecionados));
    }, [
        selectedIdEmpreendimento,
        listBlocoDropDown,
        selectedBlocosDropDown,
        getEmpreendimentoBlocoDropdownUseCase,
        getEmpreendimentoBlocoTableUseCase,
    ]);

    // Apenas centros de custo elegíveis: sem obra e sem empreendimento (exceto o do empreendimento em edição).
    const loadCentroCustoDropDown = useCallback(async (idEmpreendimento?: number | null) => {
        try {
            const centroCustoLista = await getCentroCustoEmpreendimentoDropDownUseCase.getCentroCustoEmpreendimentoDropDown(idEmpreendimento);
            setListCentroCustoDropDown([...centroCustoLista]);
        } catch (e: unknown) {
            throw new Error("Falha ao buscar centro de custo!");
        }
    }, [
        getCentroCustoEmpreendimentoDropDownUseCase,
    ]);

    useLayoutEffect(() => {
        setPageLoading(true)
    }, [
        setPageLoading,
    ]);


    useLayoutEffect(() => {
        setPageLoading(true)
    }, [
        setPageLoading,
    ]);

    //#endregion
    usePageStartBootstrap(async () => {
        try {
            setLoading(true);
            const params = new URLSearchParams(location.search);
            const locationId = params.get('id');
            const idEmpreendimento = locationId && locationId.length > 0 ? Number(locationId) : undefined;

            if (idEmpreendimento) {
                await loadCentroCustoDropDown(idEmpreendimento);
                await loadEmpreendimento(idEmpreendimento);
                await loadBlocoDropDown(idEmpreendimento);
                setSelectedIdEmpreendimento(idEmpreendimento ?? null);
            } else {
                setSelectedIdEmpreendimento(null);
                await loadCentroCustoDropDown();
            }
        } catch (e: unknown) {
            throw new Error("Falha ao buscar dados da pagina!");
        } finally {
            setLoading(false);
        }
    });

    // useEffect(() => {
    //     if (!startFlag.current) {
    //         startFlag.current = true;
    //         (async function () {
    //             try {
    //                 setLoading(true);
    //                 setErrorMessage(undefined);
    //                 const idEmpreendimento = location.state?.idEmpreendimento as number | undefined

    //                 if (idEmpreendimento) {
    //                     setSelectedIdEmpreendimento(idEmpreendimento ?? null)
    //                     await loadCentroCustoDropDown();
    //                     await loadEmpreendimento(idEmpreendimento);
    //                     await loadBlocoDropDown(idEmpreendimento);
    //                 } else {
    //                     throw new Error("ID do empreendimento não fornecido!");
    //                 }

    //             } catch (e: unknown) {
    //                 setErrorMessage(processError(e, "Falha ao buscar centro custo para lista!")[0]);
    //             } finally {
    //                 setLoading(false);
    //                 setPageLoading(false);
    //             }
    //         })();
    //     }
    // }, [
    //     location,
    //     selectedIdEmpreendimento,
    //     listBlocoDropDown,
    //     selectedBlocosDropDown,
    //     loadBlocoDropDown,
    //     loadCentroCustoDropDown,
    //     loadEmpreendimento,
    //     setPageLoading,
    // ]);

    const handlePageBack = useCallback(() => {
        navigate('/Empreendimento_lista')
    }, [navigate])


    //#region SUBMITS|EMPREENDIMENTO OPENSTATE


    const handleCadastroEmpreendimentoOpenState = useCallback(async (tipo: number | null, openState: boolean) => {
        if (await hookForm.trigger(["descricao", "idCentroCusto"])) {
            if (tipo !== null) {
                if (tipo > 0) {
                    if (openState) {

                        if (tipo && listCentroCustoDropDown) {
                            hookForm.setValue("tipo", tipo)
                        } else {
                            hookForm.setValue("tipo", 0)
                        }
                    }
                    setOpenCadastroModal(openState);
                }
            } else {
                hookForm.resetField("apartamentosQuantidade");
                hookForm.resetField("andaresQuantidade");
                hookForm.resetField("blocosQuantidade");
                setOpenCadastroModal(openState);
            }
        }

    }, [listCentroCustoDropDown, hookForm]);

    const setEmpreendimentoIdInUrl = useCallback((idEmpreendimento: number) => {
        const params = new URLSearchParams(location.search);
        params.set('id', String(idEmpreendimento));
        navigate({
            pathname: location.pathname,
            search: `?${params.toString()}`,
            hash: location.hash,
        }, { replace: true, state: location.state });
    }, [location.hash, location.pathname, location.search, location.state, navigate]);

    const handleCadatroEmpreendimentoSubmit = useCallback(async (form: EmpreendimentoForm) => {
        try {
            setLoading(true);
            setSaving(true);
            setErrorMessage(undefined);
            const empreendimento: EmpreendimentoCreate = {
                idEmpreendimento: selectedIdEmpreendimento ?? 0,
                idCentroCusto: form.idCentroCusto,
                descricao: form.descricao,
                tipo: form.tipo,
                blocosQuantidade: form.blocosQuantidade,
                andaresQuantidade: form.andaresQuantidade,
                apartamentosQuantidade: form.apartamentosQuantidade
            };

            const idEmpreendimento = await createEmpreendimentoUseCase.createEmpreendimentoUsecase(empreendimento);
            setSelectedIdEmpreendimento(idEmpreendimento)
            setEmpreendimentoIdInUrl(idEmpreendimento);
            await loadEmpreendimento(idEmpreendimento)
            await loadBlocoDropDown(idEmpreendimento);
            await loadCentroCustoDropDown(idEmpreendimento);

        } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, "Falha ao cadastrar Empreendimento!"));
        } finally {
            setSaving(false);
            setLoading(false);
            setOpenCadastroModal(false);
        }
    }, [
        createEmpreendimentoUseCase,
        selectedIdEmpreendimento,
        loadBlocoDropDown,
        loadCentroCustoDropDown,
        loadEmpreendimento,
        pushErrorNotification,
        setEmpreendimentoIdInUrl,
    ]);


    const handleUpdateEmpreendimentoSubmit = useCallback(async (form: EmpreendimentoForm) => {
        try {
            setLoading(true);
            setSaving(true);
            setErrorMessage(undefined);
            if (!selectedIdEmpreendimento) throw new Error("Um empreendimento deve ser selecionado!");

            await updateEmpreendimentoUseCase.updateEmpreendimento(
                selectedIdEmpreendimento,
                form.descricao,
                form.apresentacao
            );

            // Unidades com descrição alterada; o backend também atualiza a obraAreaChecklist vinculada.
            if (listAlteracaoUnidades.list.length > 0) {
                await updateUnidadesUseCase.updateIncorporacaoImovel(listAlteracaoUnidades.list);
                setListAlteracaoUnidades({ list: [] });
            }

            await loadEmpreendimento(selectedIdEmpreendimento)
            setEmpreendimentoIdInUrl(selectedIdEmpreendimento);
            await loadBlocoDropDown(selectedIdEmpreendimento);
            await loadCentroCustoDropDown(selectedIdEmpreendimento);

            pushInfoNotification("SUCESSO", "Empreendimento atualizado com sucesso !!", { isAutoFinish: true })
        } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, "Falha ao salvar dados!"));
        } finally {
            setSaving(false);
            setLoading(false);
            setOpenCadastroModal(false);
        }
    }, [
        selectedIdEmpreendimento,
        updateEmpreendimentoUseCase,
        listAlteracaoUnidades,
        updateUnidadesUseCase,
        loadBlocoDropDown,
        loadCentroCustoDropDown,
        loadEmpreendimento,
        setEmpreendimentoIdInUrl,
        pushInfoNotification,
        pushErrorNotification,
    ]);

    //#endregion




    //#region CHANGES | DROPDOWN CHANGES
    const handleEmpreendimentoBlocoDropDownChange = useDebouncedCallback(debouncedRef, (idBlocos: number[]) => {
        setSelectedBlocosDropDown(idBlocos);
        return async () => {
            try {
                setLoading(true)
                setListBlocoCompleteTable(await getEmpreendimentoBlocoTableUseCase.getEmpreendimentoBlocoTable(idBlocos))

            } catch (e: unknown) {
                pushErrorNotification("Erro", processError(e, "Falha ao buscar Bloco!"));
            } finally {
                setLoading(false);
            }
        }
    }, [
        getEmpreendimentoBlocoTableUseCase,
        pushErrorNotification,
    ]);

    const handleSelectBlocoAlteracao = useCallback((idBloco: number, descricao: string | null) => {
        try {
            setSelectedBlocoAlteracao({
                idBloco: idBloco,
                descricao: descricao
            })
        } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, "Falha ao salvar estado mudança de descrição de bloco!"));
        }
    }, [
        pushErrorNotification,
    ])

    const handleSelectUnidadeAlteracao = useCallback((idUnidade: number, descricao: string | null) => {
        try {
            setSelectedUnidadeAlteracao({
                idUnidade: idUnidade,
                descricao: descricao
            })
        } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, "Falha ao salvar estado mudança de descrição da unidade!"));
        }
    }, [
        pushErrorNotification,
    ])

    const handleCentroCustoDropDownChange = useCallback(async (idCentroCusto: number | null) => {
        try {
            hookForm.setValue("idCentroCusto", idCentroCusto ?? 0)
        } catch (e: unknown) {
            throw new Error("Falha ao buscar centro de custo!");
        }
    }, [
        hookForm,
    ]);

    // Guarda a nova descrição da unidade; o update no banco acontece em handleUpdateEmpreendimentoSubmit.
    const handleUnidadeTitleChange = useCallback((newName: string, idUnidade: number, idBloco: number) => {
        try {
            setListBlocoCompleteTable(prev => {
                if (!prev || !prev.list) return prev;

                const newList = prev.list.map(bloco => {
                    if (bloco.listIncorporacaoImovel[0]?.idIncorporacaoBloco !== idBloco) return bloco;

                    return {
                        ...bloco,
                        listIncorporacaoImovel: bloco.listIncorporacaoImovel.map(imovel =>
                            imovel.idIncorporacaoImovel === idUnidade ? { ...imovel, descricao: newName } : imovel
                        )
                    };
                });
                return { ...prev, list: newList };
            });

            setListAlteracaoUnidades(prev => {
                const newList = prev.list.filter(c => c.idUnidade !== idUnidade);
                return { list: [...newList, { idUnidade, descricao: newName }] };
            });

        } catch (e: unknown) {
            throw new Error("Falha ao atualizar unidade da lista!");
        }
    }, []);

    const handleBlocoTitleChange = useCallback((newName: string, idBloco: number) => {
        try {

            const index1 = listBlocoCompleteTable?.list.findIndex(c => c.listIncorporacaoImovel[0].idIncorporacaoBloco === idBloco) ?? -1;
            if (index1 >= 0) {
                setListBlocoCompleteTable(prev => {
                    if (!prev || !prev.list || !prev.list[index1]) return prev;
                    const newList = [...prev.list];
                    newList[index1] = {
                        ...newList[index1],
                        nomeBloco: newName
                    };
                    return { ...prev, list: newList };
                });
            }
            const index2 = listAlteracaoBloco.list.findIndex(c => c.idBloco === idBloco)
            if (index2 >= 0) {
                setListAlteracaoBloco(prev => {
                    var newList = [...prev.list];
                    newList[index2] = { ...newList[index2], descricao: newName };
                    updateBlocosUseCase.updateBlocos(newList);
                    return { list: newList }

                });
            } else {
                setListAlteracaoBloco(prev => {
                    var newList = [...prev.list, { idBloco: idBloco, descricao: newName }]
                    updateBlocosUseCase.updateBlocos(newList);
                    return { list: newList }
                });
            }

        } catch (e: unknown) {
            throw new Error("Falha ao atualizar blocos da lista!");
        }
    }, [
        listAlteracaoBloco,
        listBlocoCompleteTable,
        updateBlocosUseCase,
    ]);

    //#endregion





    //#region BLOCO DELETE

    const handleBlocoDeleteOpenState = useCallback((idBloco: number | null, openState: boolean) => {
        if (openState) {
            if (idBloco) {
                setSelectedBlocoDelecao({
                    idBloco: idBloco,
                    descricao: '',
                });
            } else {
                setSelectedBlocoDelecao(undefined);
            }
        }
        setOpenDeleteModal(openState);
    }, []);

    const handleConfirmarDeletarBloco = useCallback(async () => {
        try {
            if (selectedBlocoDelecao) {
                if (!selectedIdEmpreendimento) throw new Error("Um empreendimento deve ser selecionado!");

                setLoading(true);
                await deleteBlocoUseCase.deleteBloco(selectedBlocoDelecao.idBloco);
                await reloadBlocoDropDown();
                pushInfoNotification("SUCESSO", "Bloco deletado com sucesso !!", { isAutoFinish: true })
            };
        } catch (e: unknown) {
            throw new Error("Falha ao deletar bloco da lista!");
        } finally {
            setLoading(false);
            setOpenDeleteModal(false);
        }
    }, [
        selectedBlocoDelecao,
        selectedIdEmpreendimento,
        deleteBlocoUseCase,
        reloadBlocoDropDown,
        pushInfoNotification,
    ]);

    //#endregion




    //#region UNIDADE DELETE

    const handleUnidadeDeleteOpenState = useCallback((idUnidade: number | null, descricao: string | null, openState: boolean) => {
        if (openState && idUnidade) {
            setSelectedUnidadeDelecao({ idUnidade, descricao });
        } else if (!openState) {
            setSelectedUnidadeDelecao(undefined);
        }
        setOpenDeleteUnidadeModal(openState);
    }, []);

    // Deleta a unidade; se for a última do andar, o backend deleta o andar junto.
    const handleConfirmarDeletarUnidade = useCallback(async () => {
        if (!selectedUnidadeDelecao) return;

        try {
            setSaving(true);
            await deleteUnidadeUseCase.deleteUnidade(selectedUnidadeDelecao.idUnidade);

            // Descarta alteração de descrição pendente da unidade deletada.
            setListAlteracaoUnidades(prev => ({ list: prev.list.filter(c => c.idUnidade !== selectedUnidadeDelecao.idUnidade) }));
            setListBlocoCompleteTable(await getEmpreendimentoBlocoTableUseCase.getEmpreendimentoBlocoTable(selectedBlocosDropDown));

            pushInfoNotification("SUCESSO", "Unidade deletada com sucesso !!", { isAutoFinish: true })
            setOpenDeleteUnidadeModal(false);
            setSelectedUnidadeDelecao(undefined);
        } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, "Falha ao deletar unidade!"));
        } finally {
            setSaving(false);
        }
    }, [
        selectedUnidadeDelecao,
        selectedBlocosDropDown,
        deleteUnidadeUseCase,
        getEmpreendimentoBlocoTableUseCase,
        pushErrorNotification,
        pushInfoNotification,
    ]);

    //#endregion


    //#region BLOCO COPIA

    const handleCopyBlocoOpenState = useCallback((idBloco: number | null, openState: boolean) => {
        if (openState) {
            if (idBloco) {
                setSelectedBlocoCopia({
                    idBloco: idBloco,
                    descricao: '',
                });
            } else {
                setSelectedBlocoCopia(undefined);
            }
        }
        setOpenCopyModal(openState);
    }, []);

    const handleConfirmarCopiaBloco = useCallback(async (numeroCopias: string | null) => {
        try {
            if (selectedBlocoCopia) {
                const parsedNumero = Number(numeroCopias);
                if (
                    !numeroCopias ||
                    numeroCopias === "__" ||
                    isNaN(parsedNumero) ||
                    parsedNumero <= 0
                ) {
                    pushErrorNotification("ERRO", ["Informe um número válido de cópias."], { isAutoFinish: true });
                    return;
                }

                setLoading(true);
                await copiaBlocosUseCase.copiaBlocos(selectedBlocoCopia.idBloco, parsedNumero);
                pushInfoNotification("SUCESSO", "Bloco copiado com sucesso !!", { isAutoFinish: true })

                await loadEmpreendimento(selectedIdEmpreendimento ?? undefined)
                await reloadBlocoDropDown();
            };

        } catch (e: unknown) {
            throw new Error("Falha ao copiar bloco da lista!");
        } finally {
            setLoading(false);
            setOpenCopyModal(false);
        }
    }, [
        copiaBlocosUseCase,
        selectedBlocoCopia,
        selectedIdEmpreendimento,
        loadEmpreendimento,
        reloadBlocoDropDown,
        pushErrorNotification,
        pushInfoNotification,
    ]);
    //#endregion


    //#region BLOCO NOVAS UNIDADES

    const handleCadastroUnidadesOpenState = useCallback((idBloco: number | null, openState: boolean) => {
        if (openState && idBloco) {
            setSelectedBlocoUnidades(listBlocoCompleteTable?.list.find(x => x.listIncorporacaoImovel[0]?.idIncorporacaoBloco === idBloco));
        } else if (!openState) {
            setSelectedBlocoUnidades(undefined);
        }
        setOpenUnidadesModal(openState);
    }, [listBlocoCompleteTable?.list]);

    const handleConfirmarCadastroUnidades = useCallback(async (andaresQuantidade: number, unidadesQuantidade: number, idAndar: number | null) => {
        const idBloco = selectedBlocoUnidades?.listIncorporacaoImovel[0]?.idIncorporacaoBloco;
        if (!idBloco) return;

        if (andaresQuantidade <= 0 && unidadesQuantidade <= 0) {
            pushErrorNotification("ERRO", ["Informe a quantidade de andares ou de unidades."], { isAutoFinish: true });
            return;
        }

        try {
            setSaving(true);
            await createUnidadesBlocoUseCase.createUnidadesBloco(idBloco, andaresQuantidade, unidadesQuantidade, idAndar);
            pushInfoNotification("SUCESSO", "Unidades cadastradas com sucesso !!", { isAutoFinish: true })

            setListBlocoCompleteTable(await getEmpreendimentoBlocoTableUseCase.getEmpreendimentoBlocoTable(selectedBlocosDropDown));
            setOpenUnidadesModal(false);
            setSelectedBlocoUnidades(undefined);
        } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, "Falha ao cadastrar unidades no bloco!"));
        } finally {
            setSaving(false);
        }
    }, [
        createUnidadesBlocoUseCase,
        getEmpreendimentoBlocoTableUseCase,
        selectedBlocoUnidades,
        selectedBlocosDropDown,
        pushErrorNotification,
        pushInfoNotification,
    ]);
    //#endregion


    //#region HISTORY
    const handleOpenChangeHistory = useCallback((
        idTabela: number | null,
        tipoTabela: LogTipoTabela | null,
    ) => {
        if (idTabela && tipoTabela) {
            changeHistory.handleOpenChangeHistory(idTabela, tipoTabela, false);
        } else {
            if (selectedIdEmpreendimento) {
                changeHistory.handleOpenChangeHistory(selectedIdEmpreendimento, LogTipoTabela.EMPREENDIMENTO, true);
            }
        }
    }, [
        selectedIdEmpreendimento,
        changeHistory,
    ]);

    //#endregion

    return {
        listCentroCustoDropDown,
        saving,
        loading,
        openCadastroModal,
        hookForm,
        listBlocoDropDown,
        selectedBlocosDropDown,
        selectedIdEmpreendimento,
        listBlocoCompleteTable,
        idCentroCusto,
        tipo,
        selectedEmpreendimento,
        selectedBlocoAlteracao,
        selectedBlocoDelecao,
        selectedUnidadeAlteracao,
        openDeleteModal,
        openDeleteUnidadeModal,
        possuiAlteracaoUnidades: listAlteracaoUnidades.list.length > 0,
        selectedUnidadeDelecao,
        changeHistory,
        openCopyModal,
        openUnidadesModal,
        selectedBlocoUnidades,

        handleUpdateEmpreendimentoSubmit: hookForm.handleSubmit(handleUpdateEmpreendimentoSubmit),
        setSelectedBlocoAlteracao,
        setSelectedUnidadeAlteracao,
        setListBlocoDropDown,
        setSelectedBlocosDropDown,
        setListBlocoCompleteTable,
        handleCadastroEmpreendimentoOpenState,
        handleCentroCustoDropDownChange,
        handleCadatroEmpreendimentoSubmit,
        handleEmpreendimentoBlocoDropDownChange,
        handlePageBack,
        handleBlocoDeleteOpenState,
        handleConfirmarDeletarBloco,
        handleUnidadeDeleteOpenState,
        handleConfirmarDeletarUnidade,
        handleSelectBlocoAlteracao,
        handleSelectUnidadeAlteracao,
        handleBlocoTitleChange,
        handleUnidadeTitleChange,
        handleOpenChangeHistory,
        handleCopyBlocoOpenState,
        handleConfirmarCopiaBloco,
        handleCadastroUnidadesOpenState,
        handleConfirmarCadastroUnidades,
    };
}

